if (!customElements.get("category-product-tabs")) {
  customElements.define(
    "category-product-tabs",
    class CategoryProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        this.tabs.forEach((tab, index) => {
          tab.addEventListener("click", () => this.select(index));
          tab.addEventListener("keydown", (event) =>
            this.onKeydown(event, index),
          );
        });

        this.panels.forEach((panel) => this.setupCarousel(panel));
      }

      select(index, focus = false) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute("aria-selected", selected);
          tab.tabIndex = selected ? 0 : -1;
          this.panels[i].hidden = !selected;
        });
        if (focus) this.tabs[index].focus();
        this.updateCarousel(this.panels[index]);
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        const next = {
          ArrowRight: index + 1,
          ArrowLeft: index - 1,
          Home: 0,
          End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.select(next > last ? 0 : next < 0 ? last : next, true);
      }

      setupCarousel(panel) {
        const track = panel.querySelector("[data-track]");
        if (!track) return;

        track.addEventListener("scroll", () => this.updateCarousel(panel), {
          passive: true,
        });
        window.addEventListener("resize", () => this.updateCarousel(panel));

        panel
          .querySelector("[data-prev]")
          ?.addEventListener("click", () => this.scrollToPage(panel, -1, true));
        panel
          .querySelector("[data-next]")
          ?.addEventListener("click", () => this.scrollToPage(panel, 1, true));
        panel
          .querySelector("[data-dots]")
          ?.addEventListener("click", (event) => {
            const dot = event.target.closest("button[data-page]");
            if (dot) this.scrollToPage(panel, Number(dot.dataset.page));
          });

        this.updateCarousel(panel);
      }

      // Pages are one track width each; the last page is wherever the track stops scrolling.
      getPages(track) {
        return Math.max(
          1,
          Math.ceil(track.scrollWidth / track.clientWidth - 0.05),
        );
      }

      getCurrentPage(track) {
        const pages = this.getPages(track);
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2)
          return pages - 1;
        return Math.min(
          pages - 1,
          Math.round(track.scrollLeft / track.clientWidth),
        );
      }

      scrollToPage(panel, page, relative = false) {
        const track = panel.querySelector("[data-track]");
        const pages = this.getPages(track);
        const target = relative ? this.getCurrentPage(track) + page : page;
        const index = Math.max(0, Math.min(pages - 1, target));
        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        track.scrollTo({
          left: index * track.clientWidth,
          behavior: reduceMotion ? "auto" : "smooth",
        });
      }

      updateCarousel(panel) {
        const track = panel.querySelector("[data-track]");
        if (!track || panel.hidden) return;

        const pages = this.getPages(track);
        const current = this.getCurrentPage(track);

        const prev = panel.querySelector("[data-prev]");
        const next = panel.querySelector("[data-next]");
        if (prev) prev.hidden = pages < 2 || track.scrollLeft <= 2;
        if (next)
          next.hidden =
            pages < 2 ||
            track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;

        const dots = panel.querySelector("[data-dots]");
        if (!dots) return;
        if (dots.childElementCount !== (pages > 1 ? pages : 0)) {
          const label = dots.dataset.dotLabel || "";
          dots.innerHTML = "";
          if (pages > 1) {
            for (let i = 0; i < pages; i++) {
              const dot = document.createElement("button");
              dot.type = "button";
              dot.dataset.page = i;
              dot.setAttribute("aria-label", label.replace("__n__", i + 1));
              dots.appendChild(dot);
            }
          }
        }
        Array.from(dots.children).forEach((dot, i) => {
          const active = i === current;
          dot.classList.toggle("is-active", active);
          dot.setAttribute("aria-current", active ? "true" : "false");
        });
      }
    },
  );
}
